const REVEALABLE_SELECTOR = [
  "main.content-for-layout > .shopify-section",
  "main.content-for-layout > [id^='shopify-section-']",
].join(", ");

const REVEALABLE_ITEM_SELECTOR = [
  "main.content-for-layout .section-content-wrapper > *",
  "main.content-for-layout .group-block-content > *",
  "main.content-for-layout .condition-science-column",
  "main.content-for-layout .treatment-list__item",
  "main.content-for-layout .sidebar-menu__item",
  "main.content-for-layout .treatment-specs__price-card",
  "main.content-for-layout .treatment-specs__card",
  "main.content-for-layout .treatment-technologies__row",
  "main.content-for-layout .treatment-results__image-card",
  "main.content-for-layout .treatment-care__card",
  "main.content-for-layout .testimonials__card",
  "main.content-for-layout .ai-image-grid__cell",
].join(", ");

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const ABOUT_US_PARALLAX_SELECTOR =
  "main.content-for-layout[data-template*='page.aboutus'] .section[data-parallax-bg='true']";

/** @returns {HTMLElement[]} */
function getRevealableSections() {
  return Array.from(document.querySelectorAll(REVEALABLE_SELECTOR)).filter(
    (element) => element instanceof HTMLElement,
  );
}

/** @returns {HTMLElement[]} */
function getRevealableItems() {
  return Array.from(document.querySelectorAll(REVEALABLE_ITEM_SELECTOR)).filter(
    (element) => element instanceof HTMLElement,
  );
}

/** @param {HTMLElement} element */
function markVisible(element) {
  element.dataset.scrollRevealState = "visible";
}

/** @param {HTMLElement} element */
function markPending(element) {
  element.dataset.scrollRevealState = "pending";
}

/** @param {HTMLElement} element */
function markItemVisible(element) {
  element.dataset.scrollRevealItemState = "visible";
}

/** @param {HTMLElement} element */
function markItemPending(element) {
  element.dataset.scrollRevealItemState = "pending";
}

function revealAllSections() {
  for (const section of getRevealableSections()) {
    markVisible(section);
  }

  for (const item of getRevealableItems()) {
    markItemVisible(item);
  }
}

/** @param {HTMLElement} element */
function isInitiallyVisible(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return rect.top < viewportHeight * 0.88;
}

function initScrollReveal() {
  const sections = getRevealableSections();
  const items = getRevealableItems();

  if (!sections.length && !items.length) return;

  if (window.matchMedia(REDUCED_MOTION_QUERY).matches || !window.IntersectionObserver) {
    revealAllSections();
    return;
  }

  const sectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLElement) || !entry.isIntersecting) continue;

        markVisible(entry.target);
        sectionObserver.unobserve(entry.target);
      }
    },
    {
      rootMargin: "0px 0px -12% 0px",
      threshold: 0.12,
    },
  );

  /** @param {HTMLElement[]} elements */
  const processSections = (elements) => {
    for (const section of elements) {
      if (section.dataset.scrollRevealState === "visible") continue;

      if (isInitiallyVisible(section)) {
        markVisible(section);
        continue;
      }

      markPending(section);
      sectionObserver.observe(section);
    }
  };

  const itemObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLElement) || !entry.isIntersecting) continue;

        markItemVisible(entry.target);
        itemObserver.unobserve(entry.target);
      }
    },
    {
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.08,
    },
  );

  /** @param {HTMLElement[]} elements */
  const processItems = (elements) => {
    for (const item of elements) {
      if (item.dataset.scrollRevealItemState === "visible") continue;

      if (isInitiallyVisible(item)) {
        markItemVisible(item);
        continue;
      }

      markItemPending(item);
      itemObserver.observe(item);
    }
  };

  processSections(sections);
  processItems(items);

  const mainContent = document.querySelector("main.content-for-layout");
  if (!(mainContent instanceof HTMLElement)) return;

  const mutationObserver = new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => mutation.addedNodes.length > 0)) return;

    processSections(getRevealableSections());
    processItems(getRevealableItems());
  });

  mutationObserver.observe(mainContent, {
    childList: true,
    subtree: true,
  });
}

function initAboutUsParallax() {
  const sections = Array.from(document.querySelectorAll(ABOUT_US_PARALLAX_SELECTOR)).filter(
    (element) => element instanceof HTMLElement,
  );
  if (!sections.length) return;

  const parallaxSections = sections
    .map((section) => {
      const background = section.querySelector(":scope > .custom-section-background");
      if (!(background instanceof HTMLElement)) return null;

      section.dataset.parallaxActive = "true";

      return {
        section,
        inView: false,
      };
    })
    .filter((item) => item !== null);

  if (!parallaxSections.length) return;

  const maxOffset = 110;
  /** @type {number | null} */
  let rafId = null;

  /** @param {number} value */
  const clamp = (value) => Math.max(-maxOffset, Math.min(maxOffset, value));

  const reset = () => {
    for (const item of parallaxSections) {
      item.section.style.removeProperty("--section-parallax-y");
    }
  };

  const update = () => {
    rafId = null;

    if (window.matchMedia(REDUCED_MOTION_QUERY).matches || window.innerWidth < 750) {
      reset();
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    for (const item of parallaxSections) {
      if (!item.inView) continue;

      const rect = item.section.getBoundingClientRect();
      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportHeight / 2;
      const ratio = (viewportCenter - sectionCenter) / viewportHeight;
      const y = clamp(ratio * (maxOffset * 3));

      item.section.style.setProperty("--section-parallax-y", `${y.toFixed(2)}px`);
    }
  };

  const requestUpdate = () => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(update);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLElement)) continue;

        const item = parallaxSections.find((candidate) => candidate.section === entry.target);
        if (!item) continue;

        item.inView = entry.isIntersecting;
      }

      requestUpdate();
    },
    {
      rootMargin: "120px 0px",
      threshold: 0,
    },
  );

  for (const item of parallaxSections) {
    observer.observe(item.section);
  }

  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate);

  requestUpdate();
}

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      initScrollReveal();
      initAboutUsParallax();
    },
    { once: true },
  );
} else {
  initScrollReveal();
  initAboutUsParallax();
}